/** Default filter state for dashboard report list */
export const DEFAULT_REPORT_FILTERS = {
  status: [],
  result: [],
  significant: 'all', // all | yes | no
  dateFrom: '',
  dateTo: '',
  sort: 'newest',
};

export const STATUS_OPTIONS = ['Canlı', 'Taslak', 'Durduruldu'];
export const RESULT_OPTIONS = ['Kazandı', 'Kaybetti', 'Etkisiz'];
export const SORT_OPTIONS = [
  { value: 'newest', label: 'En yeni' },
  { value: 'uplift', label: 'En yüksek uplift' },
  { value: 'duration', label: 'En uzun süre' },
  { value: 'alpha', label: 'Alfabetik' },
];

const parseFormattedDate = (dateStr) => {
  if (!dateStr) return null;
  const parts = dateStr.split('.');
  if (parts.length !== 3) return null;
  const [day, month, year] = parts.map(Number);
  if (!day || !month || !year) return null;
  return new Date(year, month - 1, day);
};

const getTestDate = (test) => {
  if (test.createdAt) return new Date(test.createdAt);
  return parseFormattedDate(test.formattedStartDate);
};

export { getTestDate };

/** Parse URL search params into filter object */
export const parseFiltersFromSearchParams = (searchParams) => ({
  status: searchParams.get('status')?.split(',').filter(Boolean) || [],
  result: searchParams.get('result')?.split(',').filter(Boolean) || [],
  significant: searchParams.get('significant') || 'all',
  dateFrom: searchParams.get('dateFrom') || '',
  dateTo: searchParams.get('dateTo') || '',
  sort: searchParams.get('sort') || 'newest',
});

/** Serialize filters to URLSearchParams (omit defaults) */
export const filtersToSearchParams = (filters) => {
  const params = new URLSearchParams();
  if (filters.status?.length) params.set('status', filters.status.join(','));
  if (filters.result?.length) params.set('result', filters.result.join(','));
  if (filters.significant && filters.significant !== 'all') {
    params.set('significant', filters.significant);
  }
  if (filters.dateFrom) params.set('dateFrom', filters.dateFrom);
  if (filters.dateTo) params.set('dateTo', filters.dateTo);
  if (filters.sort && filters.sort !== 'newest') params.set('sort', filters.sort);
  return params;
};

const matchesFilters = (test, filters) => {
  if (filters.status.length && !filters.status.includes(test.status)) return false;

  if (filters.result.length) {
    const statuses = test.resultStatuses || [];
    if (!statuses.some((r) => filters.result.includes(r))) return false;
  }

  if (filters.significant === 'yes' && !test.hasSignificant) return false;
  if (filters.significant === 'no' && test.hasSignificant) return false;

  const testDate = getTestDate(test);
  if (filters.dateFrom && testDate) {
    const from = new Date(filters.dateFrom);
    from.setHours(0, 0, 0, 0);
    if (testDate < from) return false;
  }
  if (filters.dateTo && testDate) {
    const to = new Date(filters.dateTo);
    to.setHours(23, 59, 59, 999);
    if (testDate > to) return false;
  }

  return true;
};

const sortTests = (tests, sortKey) => {
  const sorted = [...tests];
  switch (sortKey) {
    case 'uplift':
      return sorted.sort(
        (a, b) => (b.maxImprovement ?? -Infinity) - (a.maxImprovement ?? -Infinity),
      );
    case 'duration':
      return sorted.sort((a, b) => (b.testDuration || 0) - (a.testDuration || 0));
    case 'alpha':
      return sorted.sort((a, b) => a.name.localeCompare(b.name, 'tr'));
    case 'newest':
    default:
      return sorted.sort((a, b) => {
        const da = getTestDate(a)?.getTime() || 0;
        const db = getTestDate(b)?.getTime() || 0;
        return db - da;
      });
  }
};

const recomputeBrandStats = (tests) => {
  const stats = { taslak: 0, durduruldu: 0, canli: 0, toplam: tests.length };
  tests.forEach((t) => {
    if (t.status === 'Canlı') stats.canli += 1;
    else if (t.status === 'Durduruldu') stats.durduruldu += 1;
    else if (t.status === 'Taslak') stats.taslak += 1;
  });
  return stats;
};

/** Apply filters + sort to grouped brand reports; returns { brands, totalCount, visibleCount } */
export const applyReportFilters = (brandGroups, filters) => {
  let totalCount = 0;
  let visibleCount = 0;

  const brands = brandGroups
    .map((brand) => {
      totalCount += brand.tests.length;
      const filtered = sortTests(
        brand.tests.filter((test) => matchesFilters(test, filters)),
        filters.sort,
      );
      visibleCount += filtered.length;

      if (!filtered.length) return null;

      return {
        ...brand,
        tests: filtered,
        stats: recomputeBrandStats(filtered),
      };
    })
    .filter(Boolean);

  return { brands, totalCount, visibleCount };
};

export const hasActiveFilters = (filters) =>
  filters.status.length > 0 ||
  filters.result.length > 0 ||
  filters.significant !== 'all' ||
  Boolean(filters.dateFrom) ||
  Boolean(filters.dateTo) ||
  filters.sort !== 'newest';
